import type * as React from 'react'
import { useId, useState } from 'react'
import type { JSX } from 'react'

import clsx from 'clsx'

import {
  AlertDialog,
  Button,
  Flex,
  Form,
  Input,
  Label,
  Paragraph,
  Text,
  toast,
} from '@buffer-mono/popcorn'

import { useIsMobileBreakpoint } from '~publish/hooks/useIsMobileBreakpoint'
import { useAppDispatch } from '~publish/legacy/store'
import { usePostCounts } from '~publish/pages/AllChannels/PostList/usePostCounts'
import { useSelectedChannel } from '~publish/pages/Channel/ChannelContext'

import commonStyles from '../../../GeneralSettings.module.css'
import styles from '../QueueActions.module.css'
import { emptyQueue } from '../../../thunks/emptyQueue'

const AlertDialogContent = (args: {
  hideModal: () => void
  channel: ReturnType<typeof useSelectedChannel>
}): JSX.Element => {
  const { hideModal, channel } = args
  const confirmationInputId = useId()
  const { counts, loading } = usePostCounts({
    organizationId: channel.organizationId,
    channelIds: [channel.id],
  })
  const dispatch = useAppDispatch()
  const [isLoading, setLoading] = useState(false)
  const [confirmationText, setConfirmationText] = useState<string>('')
  const [error, setError] = useState<string | null>(null)

  if (loading || !counts) return <></>

  const onConfirmEmptyQueue = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault()
    // Check if the confirmation text is correct
    if (!confirmationText || confirmationText !== 'Empty') {
      setError(`Please type 'Empty' to confirm.`)
      return
    }

    const profileId = channel.id
    setLoading(true)

    const result = await dispatch(emptyQueue({ profileId }))

    if (emptyQueue.fulfilled.match(result)) {
      toast.success(result.payload.message)
    } else {
      toast.error(
        `Sorry! Something went wrong while emptying your queue: ${result.error.message}. Would you be up for trying again?`,
      )
    }

    setLoading(false)
    setConfirmationText('')
    setError(null)
    hideModal()
  }

  const onConfirmationTextChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setConfirmationText(e.target.value)
    setError(null)
  }

  return (
    <AlertDialog.Content>
      <AlertDialog.Title>Empty Queue?</AlertDialog.Title>
      <AlertDialog.Description>
        <strong>{counts.queue}</strong> post
        {counts.queue === 1 ? '' : 's'} in your <strong>{channel.name}</strong>{' '}
        queue will be permanently deleted. This action cannot be undone.
      </AlertDialog.Description>
      <AlertDialog.Body>
        <Form onSubmit={onConfirmEmptyQueue} id="empty-queue-confirmation-form">
          <Form.Field name="emptyQueueConfirmation">
            <Label htmlFor={confirmationInputId}>
              Please type &apos;Empty&apos; to proceed:
            </Label>
            <Input
              id={confirmationInputId}
              onChange={onConfirmationTextChange}
              value={confirmationText}
              aria-required
              aria-describedby={
                error ? `${confirmationInputId}:error` : undefined
              }
              aria-invalid={!!error}
            />
            {error && (
              <Text
                as="span"
                color="critical"
                size="sm"
                role="alert"
                id={`${confirmationInputId}:error`}
              >
                {error}
              </Text>
            )}
          </Form.Field>
        </Form>
      </AlertDialog.Body>
      <AlertDialog.Actions>
        <AlertDialog.Cancel>
          <Button type="button" onClick={hideModal} variant="tertiary">
            Cancel
          </Button>
        </AlertDialog.Cancel>
        <AlertDialog.Action>
          <Button
            type="submit"
            form="empty-queue-confirmation-form"
            loading={isLoading}
            variant="critical"
            data-testid="empty-queue-confirm"
          >
            Empty Queue
          </Button>
        </AlertDialog.Action>
      </AlertDialog.Actions>
    </AlertDialog.Content>
  )
}

export const EmptyQueueAction = (): JSX.Element => {
  const channel = useSelectedChannel()
  const [isModalShowing, setModalShowing] = useState(false)
  const isMobile = useIsMobileBreakpoint()

  const showModal = (): void => setModalShowing(true)
  const hideModal = (): void => {
    setModalShowing(false)
  }

  // Do not show Clean Queue action if there is no profile loaded
  if (!channel) return <></>

  return (
    <>
      <Flex
        justify="between"
        className={commonStyles.fullWidth}
        gap="xl"
        align="center"
      >
        <Flex direction="column">
          <Label>Empty Queue</Label>
          <Paragraph className={commonStyles.settingsParagraph}>
            Want a fresh start? Emptying your queue will remove all updates
            within your {channel.name} queue.
          </Paragraph>
        </Flex>
        <Flex
          className={clsx(commonStyles.actionsWrapper, styles.actionsWrapper)}
        >
          <Button
            size="large"
            variant="critical"
            data-testid="empty-queue-trigger"
            onClick={showModal}
            className={clsx(commonStyles.actionButton, styles.actionButton)}
          >
            {isMobile ? 'Empty' : 'Empty Queue'}
          </Button>
        </Flex>
      </Flex>
      <AlertDialog open={isModalShowing}>
        <AlertDialogContent hideModal={hideModal} channel={channel} />
      </AlertDialog>
    </>
  )
}
